<template>
  <component
    :is="component"
    :placement="placement"
    :auto-hide="false"
    padding="p-0"
  >
    <slot v-if="!inline" :label="label" />

    <template #content="{ close }">
      <div
        :class="[
          inline ? 'border rounded' : 'md:w-80 max-w-full'
        ]"
      >
        <div class="flex items-center justify-between p-2">
          <o-button flat outline icon="chevronLeftDouble" size="sm" @click="handleYearClick(-1)" />
          <p class="font-semibold text-lg">
            {{ _year }}
          </p>
          <o-button flat outline icon="chevronRightDouble" size="sm" @click="handleYearClick(1)" />
        </div>
        <div class="p-2">
          <div v-for="(row, index) in options" :key="index" class="flex items-center">
            <o-button
              v-for="month in row"
              :key="month.value"
              flat
              :variant="_month === month.value ? 'info' : undefined"
              class="flex-1 m-px"
              @click="handleMonthClick(month.value); close()"
            >
              {{ month.label }}
            </o-button>
          </div>
        </div>
      </div>
    </template>
  </component>
</template>

<script setup lang="ts">
import { ODropdown } from '@/components/elements'

const { $i18n } = useNuxtApp()

interface Props {
  modelValue: { month: number; year: number }
  inline?: boolean
  placement?: string
}

const props = withDefaults(defineProps<Props>(), {
  inline: false,
  placement: 'bottom',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: { month: number; year: number }): void
}>()

const months = [
  { label: $i18n.t('datetime.months.january'), value: 1 },
  { label: $i18n.t('datetime.months.february'), value: 2 },
  { label: $i18n.t('datetime.months.march'), value: 3 },
  { label: $i18n.t('datetime.months.april'), value: 4 },
  { label: $i18n.t('datetime.months.may'), value: 5 },
  { label: $i18n.t('datetime.months.june'), value: 6 },
  { label: $i18n.t('datetime.months.july'), value: 7 },
  { label: $i18n.t('datetime.months.august'), value: 8 },
  { label: $i18n.t('datetime.months.september'), value: 9 },
  { label: $i18n.t('datetime.months.october'), value: 10 },
  { label: $i18n.t('datetime.months.november'), value: 11 },
  { label: $i18n.t('datetime.months.december'), value: 12 },
]

const _month = computed({
  get() {
    const month = props.modelValue.month
    if (month && month > 0 && month < 13) {
      return props.modelValue.month
    }
    return 1
  },
  set(value: number) {
    emit('update:modelValue', { month: value, year: _year.value })
  },
})

const _year = computed({
  get() {
    return props.modelValue.year
  },
  set(value: number) {
    emit('update:modelValue', { month: _month.value, year: value })
  },
})

const component = computed(() => {
  if (props.inline) {
    return 'div'
  }
  return ODropdown
})

const options = computed(() => {
  return months.reduce((acc: any[][], _, i) => {
    if (i % 3 === 0) {
      acc.push(months.slice(i, i + 3))
    }
    return acc
  }, [])
})

const label = computed(() => {
  const month = _month.value
  const year = _year.value
  const monthLabel = months.find(({ value }) => value === month)!.label
  return `${monthLabel} ${year}`
})

function handleMonthClick(value: number) {
  _month.value = value
}

function handleYearClick(increment: number) {
  _year.value = _year.value + increment
}
</script>
